import React, { useEffect, useState, useMemo } from "react";
import { useTable, usePagination, useGlobalFilter } from "react-table";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import Select from "react-select";
import { SERVER_BASE } from "../config/server";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faPlus, faEdit, faPencil } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
const BillDetails = ({ permissions }) => {
  const [securityIns, setSecurityIns] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // Flags for permissions
  const canAdd = useMemo(() => permissions?.write, [permissions]);
  const canEdit = useMemo(() => permissions?.edit, [permissions]);
  
  const docFilterOptions = [
    { label: "All", value: "all" },
    { label: "Original / Copy", value: "original_copy" },
    { label: "Inward Memo", value: "inward_memo" },
    { label: "Paper / Kanta / Parchi", value: "paper_kanta" },
  ];
  const [docFilter, setDocFilter] = useState(docFilterOptions[0]); // All

  // Fetch security entries
  useEffect(() => {
    const fetchSecurityIn = async () => {
      try {
        const response = await apiClient.get(`${SERVER_BASE}/getSecurityIn`, {
          params: { code },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSecurityIns(response.data || []);
      } catch (error) {
        console.error("Error fetching SecurityIn data:", error);
      }
    };
    fetchSecurityIn();
  }, [code, token]);

  // Separate pending and completed bills
  const applyDocumentFilter = (data) => {
    if (!docFilter || docFilter.value === "all") return data;

    switch (docFilter.value) {
      case "original_copy":
        return data.filter(
          (i) =>
            i.documentStatus === "copyDocs" ||
            i.documentStatus === "original"
        );

      case "inward_memo":
        return data.filter(
          (i) => i.documentStatus === "Inward Entry Memo Issued"
        );

      case "paper_kanta":
        return data.filter(
          (i) => i.documentStatus === "Received on Paper / Kanta / Parchi"
        );

      default:
        return data;
    }
  };


  const pendingBills = useMemo(() => {
    const base = securityIns.filter((i) => !i.billsAdded?.trim());
    return applyDocumentFilter(base);
  }, [securityIns, docFilter]);

  const completedBills = useMemo(() => {
    const base = securityIns.filter((i) => i.billsAdded?.trim());
    return applyDocumentFilter(base);
  }, [securityIns, docFilter]);


  // Columns for Pending Bills
  const pendingColumns = useMemo(() => {
    const columns = [
      { Header: "Date", accessor: "entryDate", Cell: ({ value }) => (value ? moment(value).format("DD-MMM-YY") : "-") },
      { Header: "SR #", accessor: "gsrs", Cell: ({ value }) => value || "-" },
      { Header: "Vehicle Entry", accessor: "vehicleNumber" },
      { Header: "Came From", accessor: "cameFrom" },
      {
        Header: "Document Type",
        accessor: "documentStatus",
        Cell: ({ value }) => {
          if (value === "original") return "Original Documents";
          if (value === "copyDocs") return "Copy of Documents";
          if (value === "Inward Entry Memo Issued") return "Inward Memo";
          if (value === "Received on Paper / Kanta / Parchi")
            return "Paper / Kanta / Parchi";
          return value || "-";
        },
      },
      { Header: "Name", accessor: "driverName" },
      { Header: "Mobile", accessor: "mobileNo" },
      { Header: "SI No", accessor: "siCode", Cell: ({ value }) => value || "-" },
      {
        Header: "Action",
        Cell: ({ row }) => {
          return (
            <div className="dropdown">
              <button
                className="btn btn-light btn-sm"
                type="button"
                id={`dropdownMenuButton-${row.id}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.id}`}
              >
                <li className="dropdown-item text-muted">
                  <FontAwesomeIcon icon={faPlus} />{" "}
                  {row.original.createdByName
                    ? `${row.original.createdByName} (${row.original.createdAt
                      ? moment(row.original.createdAt).format(
                        "DD-MMM-YY hh:mm A"
                      ) // 5*60 + 30 = 330
                      : ""
                    }) `
                    : "-"}
                </li>
                {row.original.updatedBy ? (
                  <li className="dropdown-item text-muted">
                    <FontAwesomeIcon icon={faPencil} />{" "}
                    {row.original.updatedByName
                      ? `${row.original.updatedByName} (${row.original.updatedAt
                        ? moment(row.original.updatedAt).format(
                          "DD-MMM-YY hh:mm A"
                        )
                        : ""
                      })`
                      : ""}
                  </li>
                ) : null}
              </ul>
            </div>
          );
        },
      },
    ];

    if (canAdd) {
      columns.unshift({
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => (
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() =>
              navigate("../AddSiBill", { state: { rowData: row.original } })
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Bills
          </button>
        ),
      });
    }

    return columns;
  }, [canAdd, navigate]);

  // Columns for Completed Bills
  const completedColumns = useMemo(() => {
    const columns = [
      { Header: "Date", accessor: "entryDate", Cell: ({ value }) => (value ? moment(value).format("DD-MMM-YY") : "-") },
      { Header: "SR #", accessor: "gsrs", Cell: ({ value }) => value || "-" },
      { Header: "Vehicle Entry", accessor: "vehicleNumber" },
      { Header: "Came From", accessor: "cameFrom" },
      {
        Header: "Document Type",
        accessor: "documentStatus",
        Cell: ({ value }) => {
          if (value === "original") return "Original Documents";
          if (value === "copyDocs") return "Copy of Documents";
          if (value === "Inward Entry Memo Issued") return "Inward Memo";
          if (value === "Received on Paper / Kanta / Parchi")
            return "Paper / Kanta / Parchi";
          return value || "-";
        },
      },
      { Header: "Name", accessor: "driverName" },
      { Header: "Mobile", accessor: "mobileNo" },
      { Header: "SI No", accessor: "siCode", Cell: ({ value }) => value || "-" },
      { Header: "Bills Added", accessor: "billsAdded" },
      { Header: "Bill Count", accessor: "billCount" },
    ];

    if (canEdit) {
      columns.unshift({
        Header: "Action",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="dropdown">
            <button
              className="btn btn-sm btn-secondary"
              type="button"
              data-bs-toggle="dropdown"
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            <ul className="dropdown-menu">
              <li>
                <button
                  className="dropdown-item"
                  onClick={() =>
                    navigate("../AddSiBill", { state: { rowData: row.original, editMode: true } })
                  }
                >
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Bill Details
                </button>
              </li>
            </ul>
          </div>
        ),
      });
    }

    return columns;
  }, [canEdit, navigate]);

  // Table instance (always defined)
  const tableInstance = useTable(
    {
      columns: activeTab === "pending" ? pendingColumns : completedColumns,
      data: activeTab === "pending" ? pendingBills : completedBills,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,

    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setPageSize,
    setGlobalFilter,

  } = tableInstance;
  const { pageIndex, pageSize, globalFilter } = state;
  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-3">Bill Details</h4>


      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "pending" ? "active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            Pending Bills
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Completed Bills
          </button>
        </li>
      </ul>
      <div className="d-flex gap-2 align-items-center">
        <input
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="form-control w-100"
          placeholder="Search..."
        />

        <Select
          options={docFilterOptions}
          value={docFilter}
          onChange={setDocFilter}
          placeholder="Filter Document Type"
          styles={{
            container: (base) => ({ ...base, width: 260 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />


        <select
          className="form-select"
          value={state.pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{ width: "auto" }}
        >
          {[5, 10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>

      <div className="table-responsive">
        <table className="table table-striped" {...getTableProps()} style={{ fontSize: '14px' }}>
          <thead className="table-light">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={activeTab === "pending" ? pendingColumns.length : completedColumns.length}
                  className="text-center py-3"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination d-flex justify-content-between align-items-center">

        <button
          className="btn btn-secondary"
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          Previous
        </button>
        Page {state.pageIndex + 1} of {pageOptions.length}
        <button
          className="btn btn-secondary"
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          Next
        </button>

      </div>
    </div>
  );
};

export default BillDetails;
